import { DatePipe } from '@angular/common';
import {
  provideHttpClient,
  withInterceptorsFromDi,
  withXhr,
} from '@angular/common/http';
import {
  NgModule,
  Injector,
  APP_INITIALIZER,
  inject,
  provideAppInitializer,
} from '@angular/core';
import { ObjectEditorService } from './core/services/object-editor.service';
import { OBJECT_EDITOR_SERVICE } from './core/services/object-editor.token';
import { DATA_GRID_ADAPTER } from '@sensika/ag-grid-kit';
import { DataGridAdapterService } from './core/services/data-grid-adapter.service';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

import {
  TranslateLoader,
  TranslateModule,
  TranslateService,
  MissingTranslationHandler,
} from '@ngx-translate/core';
import { StoreModule } from '@ngrx/store';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { provideNgToast, TOAST_POSITIONS } from 'ng-angular-popup';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import {
  QueryClient,
  provideTanStackQuery,
} from '@tanstack/angular-query-experimental';
import { withDevtools } from '@tanstack/angular-query-experimental/devtools';

import { CoreModule } from './core/core.module';
import { SharedModule } from './shared/shared.module';
import { StaticModule } from './static/static.module';
import { AppRoutingModule } from './app-routing.module';
import { HelpModule } from '@help/help.module';

import {
  CustomTranslateHttpLoader,
  MissingTranslationService,
} from './shared/missing.translation.service';
import { UtilsApiService } from './api/services/utils-api.service';
import { CookieService } from 'ngx-cookie-service';
import { StoreService } from '@services/store.service';

import { AppComponent } from './app.component';

import { environment } from '@env/environment';
import { appInitFactory } from '@factories/app-init.factory';
import { authInitFactory } from '@factories/auth-init.factory';
import { Store } from '@ngrx/store';
import { AuthFlowService } from './core/services/auth-flow.service';
import { AuthService } from './core/services/auth.service';

import { EffectsModule } from '@ngrx/effects';
import { AuthEffects } from './core/effects/auth.effects';
import { FeedEffects } from './core/effects/feed.effects';
import { ShareEffects } from './core/effects/share.effects';
import { WidgetEffects } from './core/effects/widget.effects';
import { WidgetViewPresetService } from './core/services/widget-view-preset.service';

import { reducers } from './reducers/index';

@NgModule({
  declarations: [AppComponent],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useClass: CustomTranslateHttpLoader,
        deps: [UtilsApiService],
      },
      missingTranslationHandler: {
        provide: MissingTranslationHandler,
        useClass: MissingTranslationService,
      },
    }),
    AngularSvgIconModule.forRoot(),
    StaticModule,
    FontAwesomeModule,
    CoreModule,
    AppRoutingModule, // !!! Moved after CoreModule to ensure CoreModule routes are processed first
    StoreModule.forRoot(reducers),
    EffectsModule.forRoot([
      AuthEffects,
      FeedEffects,
      WidgetEffects,
      ShareEffects,
    ]),
    SharedModule,
    HelpModule,
    !environment.production
      ? StoreDevtoolsModule.instrument({
          name: ':: Sensika ::',
          maxAge: 25,
          // * logOnly: true  -> restricts DevTools to read-only mode (recommended for Production)
          // * logOnly: false -> enables full features: action replay, time-travel, manual dispatching (good for Dev)
          // * logOnly = false -> full debug tools enabled
          // * logOnly = true -> no State mutation allowed from DevTools!
          logOnly: !environment.production,
          serialize: {
            options: {
              map: true,
            },
          },
          connectInZone: true,
        })
      : [],
  ],
  providers: [
    CookieService,
    // runtime alias for consumers that must not import the service class (import cycle), see object-editor.token.ts
    { provide: OBJECT_EDITOR_SERVICE, useExisting: ObjectEditorService },
    // the grid stack's only door into the app, see ag-grid/data-grid.adapter.ts
    { provide: DATA_GRID_ADAPTER, useExisting: DataGridAdapterService },
    {
      provide: APP_INITIALIZER,
      useFactory: appInitFactory,
      deps: [TranslateService, Injector],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: authInitFactory,
      deps: [AuthService, AuthFlowService, Store],
      multi: true,
    },
    // Root providers are lazy, but this service's changeWidgetsLanaguage
    // derivation must run from startup (see its doc comment) - construct it here.
    provideAppInitializer(() => {
      inject(WidgetViewPresetService);
    }),
    StoreService,
    DatePipe,
    provideHttpClient(withXhr(), withInterceptorsFromDi()),
    provideNgToast({
      duration: 3000,
      position: TOAST_POSITIONS.TOP_CENTER,
      showIcon: true,
    }),
    environment.production
      ? provideTanStackQuery(new QueryClient())
      : provideTanStackQuery(
          new QueryClient(),
          withDevtools(() => ({
            loadDevtools:
              localStorage.getItem('queryDevtools') === 'true' ? 'auto' : false,
          })),
        ),
  ],
})
export class AppModule {}
